import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { api, fetchData, postData } from '../services/api';
import { PATH_SEGMENTS } from '../config/paths';
import { 
  Moment, 
  MomentResponse, 
  MomentErrorResponse,
  LanguageItem,
  SpeakerItem,
  SentimentItem,
  OccurrenceItem,
  ChannelItem,
  SearchMethodItem,
  MomentCategoryItem,
} from '../types/api';
import { ResourceState } from '../types/evalform';
import { change_state, is_editable } from '../utils/formUtils';
import { 
  FormState, 
  FormErrors, 
  submitMomentData, 
  handleFormInputChange,
  handleChannelChange as handleChannelChangeUtil,
  checkNameExists as checkNameExistsUtil,
  momentToFormState
} from '../utils/momentFormUtils';
import { useLayout } from '../contexts/LayoutContext';
import { useToast } from '../contexts/ToastContext';
import { useButtonState } from '../hooks/useButtonState';
import { scrollToFirstError } from '../utils/scrollToError';
import MomentCategoryDropdown from '../components/MomentCategoryDropdown';
import '../styles/screens/MomentForm.css';

interface FormInfo {
  [key: string]: string[];
}

const MomentForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { updateHeaderProps } = useLayout();
  const { showToast } = useToast();
  const saveButtonState = useButtonState('Save', 'Saving...', 'Saved ✅');
  const submitButtonState = useButtonState('Submit', 'Submitting...', 'Submitted ✅');
  
  // Get state from location if it exists (for copy functionality)
  const copyFrom = location.state?.copyFrom as Moment | undefined;
  const isCopy = location.state?.isCopy as boolean | undefined;
  
  // Form state
  const [formState, setFormState] = useState<FormState>({
    name: '',
    mmt_category: "",
    mmt_category_id: 1,
    is_active: "True",
    is_active_id: 1,
    state_id: ResourceState.NEW,
    new_state_id: ResourceState.NEW,
    language_id: 1,
    sentiment_id: 1,
    occurrence_id: 1,
    speaker_id: 1,
    search_method_id: 1,
    prompt: '',
    examples: '',
    channels: [1] // Default to first channel
  });
  
  // Example block state
  const [exampleBlocks, setExampleBlocks] = useState<string[]>([]);
  const [editingExampleIndex, setEditingExampleIndex] = useState<number | null>(null);
  const [editingExampleValue, setEditingExampleValue] = useState('');
  const exampleInputRef = useRef<HTMLInputElement>(null);
  
  // Constants
  const [languages, setLanguages] = useState<LanguageItem[]>([]);
  const [sentiments, setSentiments] = useState<SentimentItem[]>([]);
  const [occurrences, setOccurrences] = useState<OccurrenceItem[]>([]);
  const [speakers, setSpeakers] = useState<SpeakerItem[]>([]);
  const [searchMethods, setSearchMethods] = useState<SearchMethodItem[]>([]);
  const [channels, setChannels] = useState<ChannelItem[]>([]);
  
  // Form validation
  const [errors, setErrors] = useState<FormErrors>({});
  const [info, setInfo] = useState<FormInfo>({});
  
  // Page state
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [flashMessages, setFlashMessages] = useState<{field: string, message: string}[]>([]);
  const [initialized, setInitialized] = useState(false);
  
  // Add new state for the input field
  const [newExampleText, setNewExampleText] = useState('');
  
  // Add state for undo functionality
  const [lastClearedPrompt, setLastClearedPrompt] = useState<string | null>(null);
  const [lastClearedExamples, setLastClearedExamples] = useState<string[] | null>(null);
  const [showUndoPrompt, setShowUndoPrompt] = useState(false);
  const [showUndoExamples, setShowUndoExamples] = useState(false);
  
  // Add additional state for name field undo functionality
  const [lastClearedName, setLastClearedName] = useState<string | null>(null);
  const [showUndoName, setShowUndoName] = useState(false);
  
  // Update example blocks when examples change
  useEffect(() => {
    if (formState.examples) {
      // Split by either newlines or semicolons
      let examples = formState.examples
        .split(/[;\n]/)
        .map(ex => ex.trim())
        .filter(ex => ex);
      
      setExampleBlocks(examples);
    } else {
      setExampleBlocks([]);
    }
  }, [formState.examples]);
  
  // Focus input when editing an example
  useEffect(() => {
    if (editingExampleIndex !== null && exampleInputRef.current) {
      exampleInputRef.current.focus();
    }
  }, [editingExampleIndex]);
  
  // Determine if form is editable based on current state
  const isFormEditable = !formState.state_id || is_editable(formState.state_id);
  const isViewMode = !isFormEditable;
  
  // Update form mode logic
  const getFormMode = useCallback(() => {
    if (id) {
      if (formState.state_id && !is_editable(formState.state_id)) {
        return 'view';
      }

      return 'edit';
    } else if (isCopy) {
      return 'copy';
    } else {
      return 'new';
    }
  }, [id, isCopy, formState.state_id]);
  
  const mode = getFormMode();
  
  // Set page title based on form mode
  useEffect(() => {
    const mode = getFormMode();
    console.log("Form mode =",mode)
    let title = 'New Moment';
    
    if (mode === 'edit') {
      title = 'Edit Moment';
    } else if (mode === 'view') {
      title = 'View Moment';
    } else if (mode === 'copy') {
      title = 'Copy Moment';
    }
    
    updateHeaderProps({
      pageTitle: title,
      showInteractionExId: false,
    });
  }, [getFormMode]);
  
  // Load data once
  useEffect(() => {
    if (initialized) return;
    
    const loadData = async () => {
      try {
        // Load all constant data
        const [
          languagesRes, 
          sentimentsRes, 
          occurrencesRes, 
          speakersRes, 
          searchMethodsRes,
          channelsRes
        ] = await Promise.all([
          fetchData<{languages: LanguageItem[]}>(api(PATH_SEGMENTS.LANGUAGE)),
          fetchData<{sentiments: SentimentItem[]}>(api(PATH_SEGMENTS.SENTIMENT)),
          fetchData<{occurrences: OccurrenceItem[]}>(api(PATH_SEGMENTS.OCCURRENCE)),
          fetchData<{speakers: SpeakerItem[]}>(api(PATH_SEGMENTS.SPEAKER)),
          fetchData<{search_methods: SearchMethodItem[]}>(api(PATH_SEGMENTS.SEARCH_METHOD)),
          fetchData<{channels: ChannelItem[]}>(api(PATH_SEGMENTS.CHANNEL))
        ]);
        
        // Set all the constants first
        setLanguages(languagesRes.languages || []);
        setSentiments(sentimentsRes.sentiments || []);
        setOccurrences(occurrencesRes.occurrences || []);
        setSpeakers(speakersRes.speakers || []);
        setSearchMethods(searchMethodsRes.search_methods || []);
        setChannels(channelsRes.channels || []);
        
        // Then in a separate effect, load the moment data
        // This ensures the options are available when setting form state
        setInitialized(true);
      } catch (error) {
        console.error("Error loading data:", error);
        setInitialized(true);
      }
    };
    
    loadData();
  }, [initialized]);
  
  // Add a separate effect for loading moment data after constants are loaded
  useEffect(() => {
    if (!initialized || !id && !copyFrom) return;
    
    const loadMomentData = async () => {
      if (id) {
        await loadMoment();
      } else if (copyFrom) {
        // Handle copy from state
        const copyFormState = momentToFormState(copyFrom);
        setFormState({
          ...copyFormState,
          name: `Copy of ${copyFrom.name}`,
          state_id: ResourceState.NEW,
          new_state_id: ResourceState.NEW
        });
      }
    };
    
    loadMomentData();
  }, [initialized, id, copyFrom]);
  
  // Helper functions
  const loadMoment = async () => {
    if (!id) return;
    
    try {
      const response = await fetchData<MomentResponse>(api(PATH_SEGMENTS.MOMENT, id));
      const momentData = response.moment;
      
      const newFormState = momentToFormState(momentData);
      setFormState(newFormState);
      
      setIsEditing(true);
    } catch (error) {
      console.error("Error loading moment:", error);
      
      // Reload page to moment/ for clean state
      window.location.href = '/moment/';
    }
  };
  
  // Event handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    
    // If user is typing in a field that has an undo state active, clear the undo state
    if (name === 'name' && showUndoName) {
      setShowUndoName(false);
      setLastClearedName(null);
    } else if (name === 'prompt' && showUndoPrompt) {
      setShowUndoPrompt(false);
      setLastClearedPrompt(null);
    }
    
    // Use the shared input change handler
    handleFormInputChange(e, formState, setFormState, errors, setErrors);
  };
  
  const handleExampleAdd = () => {
    setExampleBlocks([...exampleBlocks, '']);
    setEditingExampleIndex(exampleBlocks.length);
    setEditingExampleValue('');
  };
  
  const handleExampleRemove = (index: number) => {
    const updatedExamples = exampleBlocks.filter((_, i) => i !== index);
    setExampleBlocks(updatedExamples);
    
    // Update the form state with new examples joined by newlines
    setFormState(prev => ({
      ...prev,
      examples: updatedExamples.join('\n')
    }));
    
    if (editingExampleIndex === index) {
      setEditingExampleIndex(null);
    }
  };
  
  const handleExampleClick = (index: number) => {
    setEditingExampleIndex(index);
    setEditingExampleValue(exampleBlocks[index]);
  };
  
  const handleExampleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingExampleValue(e.target.value);
  };
  
  const handleExampleBlur = () => {
    if (editingExampleIndex !== null) {
      const updatedExamples = [...exampleBlocks];
      updatedExamples[editingExampleIndex] = editingExampleValue;
      
      // Filter out empty examples
      const filteredExamples = updatedExamples.filter(ex => ex.trim());
      
      setExampleBlocks(filteredExamples);
      
      // Update the form state with the new examples
      setFormState(prev => ({
        ...prev,
        examples: filteredExamples.join('\n')
      }));
      
      setEditingExampleIndex(null);
    }
  };
  
  const handleLanguageChange = (languageId: number) => {
    setFormState(prev => ({
      ...prev,
      language_id: languageId
    }));
    
    // Clear errors for this field if any
    if (errors.language_id) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.language_id;
        return newErrors;
      });
    }
  };
  
  const handleChannelChange = (channelId: number, checked: boolean) => {
    handleChannelChangeUtil(channelId, checked, formState, setFormState, errors, setErrors);
  };
  
  const handleMomentCategoryChange = (categoryId: number, categoryName: string) => {
    setFormState(prev => ({
      ...prev,
      mmt_category_id: categoryId,
      mmt_category: categoryName
    }));
    
    // Clear errors for this field if any
    if (errors.mmt_category_id) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.mmt_category_id;
        return newErrors;
      });
    }
  };
  
  // Add this function to check if a moment name already exists
  const checkNameExists = async (name: string) => {
    if (!name.trim()) return; // Don't check empty names
    
    const nameExists = await checkNameExistsUtil(name, id ? Number(id) : undefined);
    
    if (nameExists) {
      setErrors(prev => ({
        ...prev,
        name: ["Moment name already exists, please use a different name."]
      }));
      return true;
    }
    
    // If no moments found with this name, clear any name error
    if (errors.name) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.name;
        return newErrors;
      });
    }
    return false;
  };

  // Add a handler for when the name input loses focus
  const handleNameBlur = async () => {
    if (formState.name) {
      await checkNameExists(formState.name);
    }
  };

  // Also check name existence before form submission
  const handleSubmit = async (action: 'save' | 'submit') => {
    const buttonState = action === 'save' ? saveButtonState : submitButtonState;
    
    try {
      buttonState.startLoading();
      setIsLoading(true);
      
      // Determine the current mode
      const currentMode = getFormMode();
      
      // Validate form data
      if (!formState.name) {
        const nameError = { name: ['Name is required'] };
        setErrors(nameError);
        setTimeout(() => {
          scrollToFirstError(nameError);
        }, 100);
        buttonState.reset();
        setIsLoading(false);
        return;
      }

      // Check if name already exists (only for new moments)
      if (currentMode !== 'edit' && formState.name) {
        try {
          const nameExists = await checkNameExists(formState.name);
          if (nameExists) {
            const nameError = { name: ['A moment with this name already exists'] };
            setErrors(nameError);
            setTimeout(() => {
              scrollToFirstError(nameError);
            }, 100);
            buttonState.reset();
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error checking name existence:', error);
        }
      }

      // Clear any existing errors
      setErrors({});

      // Use shared submit function
      const result = await submitMomentData(formState, action, id ? Number(id) : undefined);
      
      if (result.success) {
        // Show success toast
        showToast('✅ Moment ' + result.mmt_id + ' saved successfully ', 'success', 3000);
        buttonState.showSuccess(2000);
        
        // Redirect to view page on success after a brief delay
        setTimeout(() => {
          console.log('response.mmt_id', result.mmt_id);
          navigate(`/moment/${result.mmt_id}`);
        }, 500);
      } else {
        const errorsToSet = result.errors || {};
        setErrors(errorsToSet);
        console.error('Validation errors:', result.errors);
        
        // Check if there are field errors
        const hasFieldErrors = Object.keys(errorsToSet).length > 0;
        
        if (hasFieldErrors) {
          // Scroll to first error field
          setTimeout(() => {
            scrollToFirstError(errorsToSet);
          }, 100);
        } else {
          // No field errors but still failed - show generic error toast
          showToast('❌ Save failed, try again', 'error', 3000);
        }
        
        buttonState.reset();
        setIsLoading(false);
      }
    } catch (error) {
      // Add detailed error logging
      console.error('========== ERROR DEBUG ==========');
      console.error('Submit error:', error);
      if ((error as any).response) {
        console.error('Response data:', (error as any).response.data);
        console.error('Response status:', (error as any).response.status);
      }
      console.error('=================================');
      
      // Show error toast for unexpected errors
      showToast('❌ Save failed, try again', 'error', 3000);
      buttonState.reset();
      setIsLoading(false);
    }
  };
  
  const handleCancel = () => {
    navigate(-1); // Go back
  };
  
  // Update the handleNewExampleKeyDown function to accept HTMLTextAreaElement instead of HTMLInputElement
  const handleNewExampleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && newExampleText.trim()) {
      e.preventDefault();
      
      // Create a new array with all existing examples plus the new one
      const updatedExamples = [...exampleBlocks, newExampleText.trim()];
      
      // Update the form state with the new examples joined by newlines
      setFormState({
        ...formState,
        examples: updatedExamples.join('\n')
      });
      
      // Clear the input field
      setNewExampleText('');
    }
  };
  
  // Update the clear handlers to save the cleared content
  const handleClearPrompt = () => {
    // Save the current prompt for potential undo
    setLastClearedPrompt(formState.prompt);
    setShowUndoPrompt(true);
    
    // Clear the prompt
    setFormState({
      ...formState,
      prompt: ''
    });
    
    // Also clear any errors for this field
    if (errors.prompt) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.prompt;
        return newErrors;
      });
    }
    
    // Remove the auto-hide timeout
  };

  const handleClearExamples = () => {
    // Save the current examples for potential undo
    setLastClearedExamples([...exampleBlocks]);
    setShowUndoExamples(true);
    
    // Clear examples
    setFormState({
      ...formState,
      examples: ''
    });
    setExampleBlocks([]);
    setNewExampleText('');
    
    // Also clear any errors for this field
    if (errors.examples) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.examples;
        return newErrors;
      });
    }
    
    // Remove the auto-hide timeout
  };
  
  // Add undo handlers
  const handleUndoPrompt = () => {
    if (lastClearedPrompt) {
      setFormState({
        ...formState,
        prompt: lastClearedPrompt
      });
      setLastClearedPrompt(null);
      setShowUndoPrompt(false);
    }
  };

  const handleUndoExamples = () => {
    if (lastClearedExamples) {
      setFormState({
        ...formState,
        examples: lastClearedExamples.join('\n')
      });
      setExampleBlocks(lastClearedExamples);
      setLastClearedExamples(null);
      setShowUndoExamples(false);
    }
  };
  
  // Add handlers for name field
  const handleClearName = () => {
    // Save the current name for potential undo
    setLastClearedName(formState.name);
    setShowUndoName(true);
    
    // Clear the name
    setFormState({
      ...formState,
      name: ''
    });
    
    // Also clear any errors for this field
    if (errors.name) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.name;
        return newErrors;
      });
    }
    
    // Remove the auto-hide timeout
  };

  const handleUndoName = () => {
    if (lastClearedName) {
      setFormState({
        ...formState,
        name: lastClearedName
      });
      setLastClearedName(null);
      setShowUndoName(false);
    }
  };
  
  // Update the handleExampleTextChange function
  const handleExampleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (showUndoExamples) {
      setShowUndoExamples(false);
      setLastClearedExamples(null);
    }
    setNewExampleText(e.target.value);

    // Also clear any errors for this field
    if (errors.examples) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.examples;
        return newErrors;
      });
    }
  };
  
  return (
    <div className="moment-form-container">
      {flashMessages.length > 0 && (
        <div className="flash-messages">
          {flashMessages.map((flash, index) => (
            <div key={index} className="flash-message">
              {flash.message}
            </div>
          ))}
        </div>
      )}
      
      <div className="moment-form">
        <div className="form-content">
          <div className="form-row">
            <label className="field-label" htmlFor="name">Name</label>
            <div className="input-container">
              <input
                type="text"
                id="name"
                name="name"
                value={formState.name}
                onChange={handleInputChange}
                onBlur={handleNameBlur}
                className={errors.name ? 'error-input' : ''}
                disabled={isViewMode}
              />
              {showUndoName ? (
                <div className="undo-button-container">
                  <button type="button" className="undo-button" onClick={handleUndoName}>
                    ↩
                  </button>
                </div>
              ) : formState.name && (
                <button type="button" className="clear-input-button" onClick={handleClearName} disabled={isViewMode}>
                  ×
                </button>
              )}
              {errors.name && (
                <div className="field-error">
                  {errors.name.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="form-row">
            <label className="field-label">Moment Category</label>
            <MomentCategoryDropdown
              value={formState.mmt_category_id}
              onChange={handleMomentCategoryChange}
              disabled={isViewMode}
              error={errors.mmt_category_id}
            />
          </div>
          
          <div className="form-row">
            <label className="field-label" htmlFor="genai-prompt">GenAI Prompt</label>
            <div className="genai-container">
              <div className="prompt-section">
                <div className="prompt-description">
                  Use natural language to describe what are you looking for. You may add examples of speech of interest.
                </div>
                <div className="prompt-input-wrapper">
                  <div className="input-container prompt-container">
                    <textarea
                      id="prompt"
                      name="prompt"
                      value={formState.prompt}
                      onChange={handleInputChange}
                      className={errors.prompt ? 'error-input' : ''}
                      rows={3}
                      disabled={isViewMode}
                    />
                    {showUndoPrompt ? (
                      <div className="undo-button-container">
                        <button type="button" className="undo-button" onClick={handleUndoPrompt}>
                          ↩
                        </button>
                      </div>
                    ) : formState.prompt && (
                      <button type="button" className="clear-input-button" onClick={handleClearPrompt} disabled={isViewMode}>
                        ×
                      </button>
                    )}
                    {errors.prompt && (
                      <div className="field-error">
                        {errors.prompt.map((error, index) => (
                          <div key={index}>{error}</div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button type="button" className="test-button">
                    Test
                  </button>
                </div>
              </div>
              
              <div className="unified-examples-container">
                <div className="prompt-description">
                  Add examples here
                </div>
                <div className="input-container examples-container">
                  <div className={`examples-editor ${exampleBlocks.length > 0 ? 'has-examples' : ''} ${errors.examples ? 'error-input' : ''}`}>
                    {showUndoExamples ? (
                      <div className="undo-button-container">
                        <button type="button" className="undo-button" onClick={handleUndoExamples} disabled={isViewMode}>
                          ↩
                        </button>
                      </div>
                    ) : (exampleBlocks.length > 0 || newExampleText) && (
                      <button type="button" className="clear-input-button" onClick={handleClearExamples}>
                        ×
                      </button>
                    )}
                    <div className="example-blocks">
                      {exampleBlocks.map((example, index) => (
                        <div key={index} className="example-block">
                          {editingExampleIndex === index ? (
                            <input
                              ref={exampleInputRef}
                              type="text"
                              className="example-edit-input"
                              value={editingExampleValue}
                              onChange={(e) => setEditingExampleValue(e.target.value)}
                              onBlur={handleExampleBlur}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleExampleBlur();
                                if (e.key === 'Escape') setEditingExampleIndex(null);
                              }}
                              disabled={isViewMode}
                            />
                          ) : (
                            <>
                              <span 
                                className="example-text" 
                                onClick={() => handleExampleClick(index)}
                              >
                                {example}
                              </span>
                              <button
                                type="button"
                                className="remove-example-button"
                                onClick={() => handleExampleRemove(index)}
                                disabled={isViewMode}
                              >
                                ×
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                    <textarea
                      placeholder="Type examples here and press Enter to add"
                      value={newExampleText}
                      onChange={handleExampleTextChange}
                      onKeyDown={handleNewExampleKeyDown}
                      className="examples-input"
                      rows={3}
                      disabled={isViewMode}
                    />
                  </div>
                  {errors.examples && (
                    <div className="field-error">
                      {errors.examples.map((error, index) => (
                        <div key={index}>{error}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="form-row">
            <label className="field-label">Language</label>
            <div className="radio-group">
              {languages.map(language => (
                <div key={language.id} className="radio-option">
                  <input
                    type="radio"
                    id={`language-${language.id}`}
                    name="language"
                    checked={formState.language_id === language.id}
                    onChange={() => handleLanguageChange(language.id)}
                    disabled={isViewMode}
                  />
                  <label htmlFor={`language-${language.id}`}>{language.name}</label>
                </div>
              ))}
            </div>
            {errors.language_id && (
              <div className="field-error">
                {errors.language_id.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </div>
            )}
          </div>
          
          <div className="form-row three-column-row">
            <div className="form-column">
              <label className="field-label" htmlFor="speaker_id">Speaker</label>
              <select
                id="speaker_id"
                name="speaker_id"
                value={String(formState.speaker_id)}
                onChange={handleInputChange}
                className={errors.speaker_id ? 'error-input' : ''}
                disabled={isViewMode}
              >
                <option value="" disabled>Select a speaker</option>
                {speakers.map(speaker => (
                  <option 
                    key={speaker.id} 
                    value={String(speaker.id)}
                  >
                    {speaker.name}
                  </option>
                ))}
              </select>
              {errors.speaker_id && (
                <div className="field-error field-error-column">
                  {errors.speaker_id.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="form-column">
              <label className="field-label" htmlFor="occurrence_id">Occurrence</label>
              <select
                id="occurrence_id"
                name="occurrence_id"
                value={String(formState.occurrence_id)}
                onChange={handleInputChange}
                className={errors.occurrence_id ? 'error-input' : ''}
                disabled={isViewMode}
              >
                <option value="" disabled>Select an occurrence</option>
                {occurrences.map(occurrence => (
                  <option 
                    key={occurrence.id} 
                    value={String(occurrence.id)}
                  >
                    {occurrence.name}
                  </option>
                ))}
              </select>
              {errors.occurrence_id && (
                <div className="field-error field-error-column">
                  {errors.occurrence_id.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="form-column">
              <label className="field-label" htmlFor="sentiment_id">Sentiment</label>
              <select
                id="sentiment_id"
                name="sentiment_id"
                value={String(formState.sentiment_id)}
                onChange={handleInputChange}
                className={errors.sentiment_id ? 'error-input' : ''}
                disabled={isViewMode}
              >
                <option value="" disabled>Select a sentiment</option>
                {sentiments.map(sentiment => (
                  <option 
                    key={sentiment.id} 
                    value={String(sentiment.id)}
                  >
                    {sentiment.name}
                  </option>
                ))}
              </select>
              {errors.sentiment_id && (
                <div className="field-error field-error-column">
                  {errors.sentiment_id.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="form-row">
            <label className="field-label">Channel</label>
            <div className="checkbox-group channel-checkboxes">
              {channels.map(channel => (
                <div key={channel.id} className="checkbox-option">
                  <input
                    type="checkbox"
                    id={`channel-${channel.id}`}
                    checked={formState.channels.includes(channel.id)}
                    onChange={(e) => handleChannelChange(channel.id, e.target.checked)}
                    disabled={isViewMode}
                  />
                  <label htmlFor={`channel-${channel.id}`}>{channel.name}</label>
                </div>
              ))}
            </div>
            {errors.channels && (
              <div className="field-error">
                {errors.channels.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="form-actions">
          <button 
            type="button" 
            className="cancel-button" 
            onClick={handleCancel}
            disabled={isViewMode}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="save-button" 
            onClick={() => handleSubmit('save')}
            disabled={isViewMode || saveButtonState.isLoading || submitButtonState.isLoading}
          >
            {saveButtonState.buttonText}
          </button>
          <button 
            type="button" 
            className="submit-button" 
            onClick={() => handleSubmit('submit')}
            disabled={isViewMode || saveButtonState.isLoading || submitButtonState.isLoading}
          >
            {submitButtonState.buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MomentForm; 